import { CircularProgress, ComboBox } from '@civilgrid/ui/components';
import { useEffect, useState } from 'react';
import { getAllOrganizations } from '~/api/internal';
import type { OrganizationDetail } from '~/common/types';
import { Notify } from '~/components2/Notify';

interface OrganizationPickerProps {
  selectedOrganization: OrganizationDetail | null;
  onOrganizationSelect: (organizationId: string, organization: OrganizationDetail) => void;
  placeholder?: string;
  searchPlaceholder?: string;
}

export default function OrganizationPicker({
  selectedOrganization,
  onOrganizationSelect,
  placeholder = 'Select Organization',
  searchPlaceholder = 'Search organizations...',
}: OrganizationPickerProps) {
  const [organizations, setOrganizations] = useState<OrganizationDetail[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch all organizations on component mount
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setIsLoading(true);
        const orgs = await getAllOrganizations();
        setOrganizations(orgs);
      } catch (error) {
        console.error('Error fetching organizations:', error);
        Notify.error({ message: 'Error loading organizations' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  if (isLoading) {
    return <CircularProgress indeterminate />;
  }

  const activeOrganizations = organizations.filter((org) => org.status === 'active');

  return (
    <ComboBox
      options={activeOrganizations.map((org) => ({ value: org.id, label: org.name }))}
      value={selectedOrganization?.id ?? ''}
      onValueChange={(organizationId) => {
        const organization = activeOrganizations.find((org) => org.id === organizationId);
        if (organization) onOrganizationSelect(organization.id, organization);
      }}
      allowDeselect={false}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      emptyText="No organization found."
      aria-label="Organization"
      data-testid="organization-picker"
    />
  );
}
